import * as React from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { cn } from '../../lib/cn';
import { CHART_SERIES } from './palette';
import { useReducedMotion } from '../../hooks/use-reduced-motion';

export interface DonutSlice {
  label: string;
  value: number;
}

export interface DonutChartProps {
  data: DonutSlice[];
  /** Color palette in slice order. Default = the shared `CHART_SERIES` categorical palette. */
  colors?: readonly string[];
  /** Renders a centered total in the donut hole. */
  centerLabel?: string;
  centerValue?: React.ReactNode;
  emptyMessage?: string;
  className?: string;
  height?: number;
}

// Was an 8-entry accent/secondary ramp that included `--accent-200`
// (1.75:1 against light `--bg-surface` — well under the 3:1 SC 1.4.11
// floor for a meaning-carrying mark). `CHART_SERIES` is the palette built
// and contrast-checked for exactly this job — see `palette.ts`. It wraps
// at 6 rather than 8; a donut with more than 6 slices should bucket the
// remainder into an "Other" catch-all rather than lean on a 7th/8th hue.
const DEFAULT_PALETTE = CHART_SERIES;

const DEFAULT_HEIGHT = 240;

const fmtNum = (n: number) =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);

export function DonutChart({
  data,
  colors = DEFAULT_PALETTE,
  centerLabel,
  centerValue,
  emptyMessage = 'No data',
  className,
  height = DEFAULT_HEIGHT,
}: DonutChartProps) {
  const total = data.reduce((s, d) => s + d.value, 0);
  // The Pie slice itself is a path, not a Bar rectangle, so it does not
  // suffer the "absent from the DOM at t=0" bug — but it still spins up
  // regardless of `prefers-reduced-motion`, which `--duration-chart: 0ms`
  // (packages/tokens/src/tokens.css) promises to disable. Recharts reads
  // duration from a JS prop, never the CSS variable, so this is the bridge.
  const reduceMotion = useReducedMotion();

  if (data.length === 0 || total === 0) {
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-lg border border-dashed border-border bg-bg-surface text-text-muted',
          className,
        )}
        style={{ height }}
      >
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className={cn('rounded-lg border border-border bg-bg-surface p-4', className)}>
      <div className="relative" style={{ height }}>
        <ResponsiveContainer width="100%" height={height}>
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="label"
              innerRadius="60%"
              outerRadius="90%"
              paddingAngle={2}
              stroke="var(--bg-surface)"
              strokeWidth={2}
              isAnimationActive={!reduceMotion}
              animationDuration={600}
            >
              {data.map((_, idx) => (
                <Cell key={idx} fill={colors[idx % colors.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--bg-surface)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                fontSize: 12,
                color: 'var(--text-primary)',
              }}
              formatter={(value: number, name: string) => [fmtNum(value), name]}
            />
          </PieChart>
        </ResponsiveContainer>
        {(centerValue || centerLabel) && (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            {centerLabel ? (
              <div className="text-caption text-text-muted">{centerLabel}</div>
            ) : null}
            {centerValue ? (
              <div className="text-h2 font-medium text-text-primary tabular-nums">{centerValue}</div>
            ) : null}
          </div>
        )}
      </div>
      <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-caption">
        {data.map((d, idx) => (
          <li key={d.label} className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2 truncate">
              <span
                className="inline-block h-2.5 w-2.5 rounded-sm"
                style={{ backgroundColor: colors[idx % colors.length] }}
                aria-hidden
              />
              <span className="truncate text-text-secondary">{d.label}</span>
            </span>
            <span className="tabular-nums text-text-primary">{fmtNum(d.value)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
